import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterOption } from "@models/grid/grid-filter";
import {
  ExportDocumentsListComponent
} from "@pages/documents/components/documents-page/components/export-documents-list/export-documents-list.component";
import { DocumentsGridService } from "@pages/documents/services/documents-grid.service";
import { DocumentTypePipe } from "@shared/pipes/document-type.pipe";
import { excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ExcelExportParams,
  ExcelRow,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import { deepClone } from "fast-json-patch";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class DocumentsExportService {
  private modalFormsService = inject(ModalFormsService);
  private documentsGridService = inject(DocumentsGridService);

  // exportSelectedDocumentsToXls(): void {
  //   this.exportDocumentsList(true, ExportTypeEnum.EXCEL);
  // }

  exportDocumentsList(
    selectedDocuments: DocumentRecord[],
    isBulkExport: boolean,
    exportType: ExportTypeEnum
  ): void {
    const gridApi = this.documentsGridService.gridApi;
    const colDefs = this.documentsGridService.colDefs();

    const modalRef = this.modalFormsService.openModal(ExportDocumentsListComponent, { modalDialogClass: 'export-documents-list' });
    const componentInstance = modalRef.componentInstance as ExportDocumentsListComponent;

    componentInstance.colDefs = deepClone(colDefs) as ColumnWithExportName[];
    componentInstance.numberOfDocuments = selectedDocuments.length ? selectedDocuments.length : this.documentsGridService.totalRecords();
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const fileName = 'Documents ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US');
        const onlySelected = selectedDocuments.length > 0;
        const numberColumnForExport = columnForExport.length - 1;
        const exportParamsXls = this.exportParamsXls(numberColumnForExport) as ExcelExportParams;

        if (onlySelected) {
          const params: ExcelExportParams = {
            columnKeys: columnForExport,
            onlySelected: true
          };
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
        } else {
          const endRow = this.documentsGridService.totalRecords();

          this.documentsGridService.dataLoaded$
            .pipe(take(1))
            .subscribe((result) => {
              if (exportType === ExportTypeEnum.EXCEL) {
                const params: ExcelExportParams = isBulkExport ? {
                  columnKeys: columnForExport,
                  onlySelected: true
                } : { columnKeys: columnForExport };
                gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
              }
            });

          this.documentsGridService.setDataSource('', endRow);
        }
      });
  }

  exportParamsXls(numberColumnForExport: number = 0): ExcelExportParams {
    return {
      ...this.getExportParams(),
      prependContent: this.getExcelTitleRows(numberColumnForExport),
    } as ExcelExportParams;
  }

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) =>
    excelTitleForReport(numberColumnForExport, 'Documents Report');

  getExportParams() {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const columnId = params.column.getColId();

        if (columnId === 'type') {
          return DocumentTypePipe.formatValue(value as string);
        }

        if (columnId === 'status' || columnId === 'asicStatus' || columnId === 'signingStatus') {
          const filterParams = params.column.getColDef()?.filterParams as { filterOptions?: FilterOption[] };
          const options: FilterOption[] = filterParams?.filterOptions ?? [];
          return options.find(o => o.value == value)?.label ?? '';
        }

        if (
          columnId === 'createdOn'
          || columnId === 'lodgementDeadline'
          || columnId === 'modifiedOn'
          || columnId === 'dateSigned'
          || columnId === 'lodgedOn'
        ) {
          return value ? formatDate(value as string ?? '', 'dd MMM yyyy', 'en-US') : "";
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName: 'Documents',
    };
  }
}
